import { DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { GetSparkCampaignForBriefAdGroupDto } from '@/app/common/general-types/tiktok-spark-ads.dto';
import { SparkAdGroupColumnPickerComponent } from '../spark-ad-group-column-picker/spark-ad-group-column-picker';
import {
  AdGroupColumnKey,
  DEFAULT_AD_GROUP_COLUMNS,
} from './spark-ad-groups-table-columns';
import { ManageBriefTabsService } from '@/app/pages/manage-brief-tabs/service/manage-brief-tabs/manage-brief-tabs.service';
import { Router } from '@angular/router';
import { CreateSparkCampaign } from '../../service/create-spark-campaign/create-spark-campaign';

@Component({
  selector: 'bttv2-spark-ad-groups-table',
  imports: [SparkAdGroupColumnPickerComponent, DecimalPipe],
  templateUrl: './spark-ad-groups-table.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SparkAdGroupsTableComponent {
  adGroups = input.required<GetSparkCampaignForBriefAdGroupDto[]>();

  visibleColumns = signal<Set<AdGroupColumnKey>>(new Set(DEFAULT_AD_GROUP_COLUMNS));
  searchQuery = signal('');

  private router = inject(Router);
  private manageBriefTabsService = inject(ManageBriefTabsService);
  public createSparkCampaignService = inject(CreateSparkCampaign);

  filteredAdGroups = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    if (!query) {
      return this.adGroups();
    }
    return this.adGroups().filter(
      g =>
        (g.adgroupName ?? '').toLowerCase().includes(query) ||
        (g.id ?? '').toLowerCase().includes(query) ||
        (g.tiktokAdGroupId ?? '').toLowerCase().includes(query),
    );
  });

  isVisible(key: AdGroupColumnKey): boolean {
    return this.visibleColumns().has(key);
  }

  onColumnsChange(cols: Set<AdGroupColumnKey>): void {
    this.visibleColumns.set(cols);
  }

  onSearch(event: Event): void {
    this.searchQuery.set((event.target as HTMLInputElement).value);
  }

  formatSparkDate(iso: string | null | undefined): string {
    if (!iso) {
      return '—';
    }
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) {
      return iso;
    }
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  }

  formatSparkBudget(amount: number | null | undefined): string {
    if (amount === undefined || amount === null) {
      return '—';
    }
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    }).format(amount);
  }

  sparkBudgetModeLabel(mode: string | null | undefined): string {
    if (!mode) {
      return '—';
    }
    switch (mode) {
      case 'BUDGET_MODE_DAY':
        return 'Daily';
      case 'BUDGET_MODE_TOTAL':
        return 'Total';
      case 'BUDGET_MODE_INFINITE':
        return 'Unlimited';
      default:
        return mode.replace(/^BUDGET_MODE_/, '').split('_').join(' ');
    }
  }

  sparkOperationStatusLabel(status: string | null | undefined): string {
    if (!status) {
      return '—';
    }
    const s = status.toUpperCase();
    if (s === 'ENABLE' || s.includes('ACTIVE')) {
      return 'Active';
    }
    if (s === 'DISABLE' || s.includes('DELETE')) {
      return 'Disabled';
    }
    if (s.includes('PAUSE') || s.includes('FROZEN')) {
      return 'Paused';
    }
    return status
      .replace(/^ADGROUP_STATUS_/, '')
      .replace(/^CAMPAIGN_STATUS_/, '')
      .split('_')
      .map(w => w.charAt(0) + w.slice(1).toLowerCase())
      .join(' ');
  }

  sparkOperationStatusClass(status: string | null | undefined): string {
    if (!status) {
      return 'bg-gray-100 text-gray-800 ring-1 ring-gray-500/10 dark:bg-white/10 dark:text-gray-200 dark:ring-white/10';
    }
    const s = status.toUpperCase();
    if (s === 'ENABLE' || s.includes('ACTIVE')) {
      return 'bg-emerald-500/15 text-emerald-900 ring-1 ring-emerald-600/20 dark:text-emerald-100 dark:ring-emerald-400/25';
    }
    if (s === 'DISABLE' || s.includes('DELETE')) {
      return 'bg-gray-100 text-gray-800 ring-1 ring-gray-500/10 dark:bg-white/10 dark:text-gray-200 dark:ring-white/10';
    }
    if (s.includes('PAUSE') || s.includes('FROZEN')) {
      return 'bg-amber-500/15 text-amber-950 ring-1 ring-amber-600/25 dark:text-amber-100 dark:ring-amber-400/30';
    }
    return 'bg-primary/10 text-accent ring-1 ring-primary/25 dark:ring-primary/30';
  }

  formatSecondaryOrOperationStatus(group: GetSparkCampaignForBriefAdGroupDto): string {
    if (group.secondaryStatus) {
      return this.humanizeApiToken(group.secondaryStatus);
    }
    if (group.operationStatus) {
      return this.humanizeApiToken(group.operationStatus);
    }
    return '—';
  }

  formatScheduleCell(group: GetSparkCampaignForBriefAdGroupDto): { start: string; end: string } {
    return {
      start: this.formatSparkDate(group.scheduleStartTime ?? undefined),
      end: this.formatSparkDate(group.scheduleEndTime ?? undefined),
    };
  }

  humanizeApiToken(raw: string | null | undefined): string {
    if (!raw) {
      return '—';
    }
    return raw
      .replace(/^(ADGROUP_|BID_|PLACEMENT_|GENDER_)/i, '')
      .split('_')
      .filter(Boolean)
      .map(w => w.charAt(0) + w.slice(1).toLowerCase())
      .join(' ');
  }

  formatStringList(values: string[] | null | undefined): string {
    if (!values?.length) {
      return '—';
    }
    return values.join(', ');
  }

  formatLocationIds(ids: string[] | null | undefined): string {
    if (!ids?.length) {
      return '—';
    }
    return ids.length + ' selected';
  }

  goToAdGroupCreate() {
    const briefId = this.manageBriefTabsService.focussedBrief()?.id;
    if (this.createSparkCampaignService.currentSparkCampaign() !== undefined && briefId) {
      this.router.navigate(['/ad-group-create', briefId, this.createSparkCampaignService.currentSparkCampaign()!.id!]);
    }
  }

  focusAdGroup(group: GetSparkCampaignForBriefAdGroupDto) {
    this.createSparkCampaignService.currentSparkAdGroup.set(group);
  }
}
